import { inject, Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular/standalone';
import { AdminAnnouncerService } from '../a11y/admin-announcer.service';

export interface PromptField {
  name: string;
  type: 'text' | 'number';
  placeholder: string;
  value?: string | number;
  /** The field's name for screen readers; defaults to the placeholder, which a prefilled value hides. */
  label?: string;
}

/**
 * A prompt's values as text. Ionic hands back a prefilled field the admin didn't touch as it was
 * given (the number 24, not "24"), which broke string checks like `.trim()`.
 */
export function promptValues(values: Record<string, unknown> | null | undefined): Record<string, string> {
  return Object.fromEntries(
    Object.entries(values ?? {}).map(([k, v]) => [k, v === null || v === undefined ? '' : String(v)]),
  );
}

/** Checks a prompt's values before it closes: an error message keeps it open, typing intact. */
export type PromptCheck = (values: Record<string, string>) => string | null | undefined;

export interface ConfirmOptions {
  /** A destructive action: the confirm button turns red. Name the action in `confirmText` ("Delete user"). */
  danger?: boolean;
  /** Keep the message's line breaks (one item per line). */
  lines?: boolean;
}

/** Promise-based yes/no confirm — replaces the @curbnturf/alert facade for admin actions. */
@Injectable({ providedIn: 'root' })
export class ConfirmService {
  private readonly alerts = inject(AlertController);
  private readonly announcer = inject(AdminAnnouncerService);

  async confirm(header: string, message = '', confirmText = 'Yes', options: ConfirmOptions = {}): Promise<boolean> {
    const alert = await this.alerts.create({
      header,
      message,
      cssClass:
        [options.danger && 'admin-alert-danger', options.lines && 'admin-alert-lines'].filter(Boolean).join(' ') ||
        undefined,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: confirmText, role: 'confirm' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role === 'confirm';
  }

  /** A notice with one OK button; the message keeps its line breaks (one reason per line). */
  async alert(header: string, message: string, okText = 'OK'): Promise<void> {
    const alert = await this.alerts.create({
      header,
      message,
      cssClass: 'admin-alert-lines',
      buttons: [{ text: okText, role: 'confirm' }],
    });
    await alert.present();
    await alert.onDidDismiss();
  }

  /**
   * Confirm with input fields; resolves to the entered values, or undefined if cancelled.
   * With `check`, an invalid entry keeps the prompt open with the error under the message
   * (and read out), instead of closing it and losing what was typed.
   */
  async prompt(
    header: string,
    message: string,
    inputs: PromptField[],
    confirmText = 'Confirm',
    check?: PromptCheck,
  ): Promise<Record<string, string> | undefined> {
    const alert = await this.alerts.create({
      header,
      message,
      cssClass: check ? 'admin-alert-lines' : undefined,
      inputs: inputs.map(({ label, ...field }) => ({
        ...field,
        attributes: { 'aria-label': label ?? field.placeholder },
      })),
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: confirmText,
          role: 'confirm',
          handler: (values: Record<string, unknown>) => {
            const error = check?.(promptValues(values));
            if (!error) return true;
            alert.message = message ? `${message}\n\n${error}` : error;
            this.announcer.announce(error);
            return false;
          },
        },
      ],
    });
    await alert.present();
    const { role, data } = await alert.onDidDismiss<{ values: Record<string, string> }>();
    return role === 'confirm' ? promptValues(data?.values) : undefined;
  }
}
